import { TrendingDown, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData } from "../../data/LiteDataProvider";
import type { Summary } from "../../lib/stats";
import { Gauge, Sparkline } from "../../ui/Charts";
import { Bar, Pnl, cardCls } from "../../ui/ui";

function Kpi({ label, children, footer }: { label: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <section className={`${cardCls} p-4 flex flex-col`}>
      <div className="text-xs text-muted-foreground mb-2">{label}</div>
      <div className="flex-1">{children}</div>
      {footer && <div className="mt-2 pt-2 border-t border-border">{footer}</div>}
    </section>
  );
}

function Delta({ value, unit = "money", suffix }: { value: number; unit?: "money" | "pts"; suffix: string }) {
  const t = useTranslation().lite.dashboard.kpi;
  const { fmt } = useLiteData();
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const text = unit === "money" ? fmt.money(value) : t.pts(`${up ? "+" : "−"}${fmt.num(Math.abs(value), 1)}`);
  return (
    <span className="inline-flex items-center gap-1 text-xs min-w-0">
      <Icon size={13} className={`shrink-0 ${up ? "text-pnlPositive" : "text-pnlNegative"}`} />
      <span className={`font-mono tabular-nums ${up ? "text-pnlPositive" : "text-pnlNegative"}`}>{text}</span>
      <span className="text-muted-foreground/70 truncate">{suffix}</span>
    </span>
  );
}

function RatioBar({ label, value, max, tone }: { label: string; value: number; max: number; tone: "gain" | "loss" }) {
  const { fmt } = useLiteData();
  return (
    <div className="grid grid-cols-[40px_1fr_64px] items-center gap-2">
      <span className="text-muted-foreground">{label}</span>
      <Bar value={value / (max || 1)} tone={tone} />
      <span className={`font-mono tabular-nums text-right ${tone === "gain" ? "text-pnlPositive" : "text-pnlNegative"}`}>{fmt.money(value, { sign: false })}</span>
    </div>
  );
}

export function KpiTiles({ s, prev, days, winDays, sparkline, maxDd, currentDd }: {
  s: Summary;
  prev: Summary | null;
  days: number;
  winDays: number;
  sparkline: number[];
  maxDd: number;
  currentDd: number;
}) {
  const t = useTranslation().lite;
  const k = t.dashboard.kpi;
  const { fmt } = useLiteData();
  const hasPrev = !!prev && prev.count > 0;
  const wl = k.winsLosses(s.wins, s.losses);
  const pfLabel = s.profitFactor >= 2 ? k.pfExcellent : s.profitFactor >= 1.5 ? k.pfSolid : s.profitFactor >= 1 ? k.pfFragile : k.pfNegative;
  const ratioMax = Math.max(s.avgWin, s.avgLoss);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-4">
      <Kpi label={k.net} footer={hasPrev && <Delta value={s.netPnl - prev!.netPnl} suffix={k.vsPrevious} />}>
        <Pnl value={s.netPnl} className="text-2xl font-semibold" />
        <div className="text-xs text-muted-foreground mt-0.5">{t.common.trades(s.count)} · {t.common.days(days)}</div>
        <div className="mt-2 -mx-1"><Sparkline values={sparkline} height={32} /></div>
      </Kpi>

      <Kpi label={k.winRate} footer={hasPrev && <Delta value={(s.winRate - prev!.winRate) * 100} unit="pts" suffix={k.vsPreviousShort} />}>
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="font-mono tabular-nums text-2xl font-semibold">{fmt.pct(s.winRate, 1)}</div>
            <div className="text-xs mt-0.5 font-mono tabular-nums">
              <span className="text-pnlPositive">{wl.w}</span> <span className="text-muted-foreground/70">·</span> <span className="text-pnlNegative">{wl.l}</span>
            </div>
          </div>
          <Gauge value={s.winRate} tone={s.winRate >= 0.5 ? "gain" : "warn"} size={72} />
        </div>
      </Kpi>

      <Kpi label={k.profitFactor} footer={<span className="text-xs text-muted-foreground/70">{pfLabel}</span>}>
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="font-mono tabular-nums text-2xl font-semibold">{fmt.num(s.profitFactor)}</div>
            <div className="text-xs text-muted-foreground mt-0.5 font-mono tabular-nums truncate">
              {fmt.money(s.grossWin, { sign: false })} / {fmt.money(s.grossLoss, { sign: false })}
            </div>
          </div>
          <Gauge value={Math.min(1, s.profitFactor / 3)} tone={s.profitFactor >= 1 ? "gain" : "loss"} size={72} />
        </div>
      </Kpi>

      <Kpi label={k.ratio}>
        <div className="font-mono tabular-nums text-2xl font-semibold">{s.avgLoss ? fmt.num(s.avgWin / s.avgLoss) : "-"}</div>
        <div className="space-y-1.5 mt-2.5 text-xs">
          <RatioBar label={k.gain} value={s.avgWin} max={ratioMax} tone="gain" />
          <RatioBar label={k.loss} value={s.avgLoss} max={ratioMax} tone="loss" />
        </div>
      </Kpi>

      <Kpi label={k.expectancy} footer={<span className="text-xs text-muted-foreground/70">{k.winDays(fmt.pct(winDays / Math.max(1, days)))}</span>}>
        <Pnl value={s.expectancy} className="text-2xl font-semibold" decimals />
        <div className="text-xs text-muted-foreground mt-0.5">{k.avgDuration(fmt.duration(s.avgHoldMs))}</div>
      </Kpi>

      <Kpi
        label={k.maxDrawdown}
        footer={<span className="text-xs text-muted-foreground/70">{k.current} <span className="font-mono tabular-nums text-pnlNegative">{fmt.money(currentDd)}</span></span>}
      >
        <div className="font-mono tabular-nums text-2xl font-semibold text-pnlNegative">{fmt.money(maxDd)}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{k.peakToTrough}</div>
      </Kpi>
    </div>
  );
}
